import React, { useCallback, useEffect, useState, useRef } from 'react';
import Order, { getOrderDisplayNumber } from '../domain/order';
import { Alert, Box, Button, Checkbox, Grid, TextField, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import FilterOrder from './filterorder';
import moment from 'moment';
import AddressEditDialog from '../common/addresseditdialog';
import { formatCurrency } from '../common/common';
import CustomerAutoComplete from '../common/customerautocomplete';
import { getCustomerFull } from '../modules/customerdata';
import { getBlockedDeliveryMethods, allocatePalletsForOrders } from '../modules/orderdata';
import {
    getBlockedDeliveryMethods as getCustomerBlockedDeliveryMethods,
    allocatePalletsForOrders as allocatePalletsForCustomerOrders,
} from '../modules/customerorderdata';
import Customer from '../domain/customer';
import DeliveryDatePicker from './deliveryDatePicker';
import OrderTypeSelector from './ordertypeselector';
import DeleteConfirmDialog from '../common/deleteconfirmdialog';
import { useDispatch } from 'react-redux';
import { filter, some, find, clone, sumBy, flatten } from 'lodash';
import EmailInput from '../common/emailinput';
import ParameterSelector from '../common/parameterselector';
import DeleteButton from '../common/deletebutton';
import ConfirmationDialog from '../common/confirmationdialog';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import FilterProductSelectDialog from '../location/filterproductselectdialog';
import DeliveryDateDetails from './deliverydatedetails';
import WarehouseSelector from './warehouseselector';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import BillingEditDialog from './billingeditdialog';
import { normalizePhonenumber } from '../common/common';
var isSameOrBefore = require('dayjs/plugin/isSameOrBefore');
var isSameOrAfter = require('dayjs/plugin/isSameOrAfter');
dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const styles = {
    textField: { ml: 1, mr: 1, width: '95%' },
};

export default function OrderItem(props) {
    const dispatch = useDispatch();

    const [confirmDeleteRowsOpen, setConfirmDeleteRowsOpen] = useState(false);
    const [alertDialogOpen, setAlertDialogOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const [removingMe, setRemovingMe] = useState(false);
    const [addressEditOpen, setAddressEditOpen] = useState(false);
    const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
    const [selectedDeliveryCustomer, setSelectedDeliveryCustomer] = useState(null);
    const [ignoreDateTimeChangeTimeoutHandle, setIgnoreDateTimeChangeTimeoutHandle] = useState(null);
    const [rowInEditId, setRowInEditId] = useState(null);
    const [ordersSelected, setOrdersSelected] = useState({});
    const [allSelected, setAllSelected] = useState(false);
    const [confirmDeliveryTimeResetOpen, setConfirmDeliveryTimeResetOpen] = useState(false);
    const [addNewProdDialogOpen, setAddNewProdDialogOpen] = useState(false);
    const [addProdToOrderId, setAddProdToOrderId] = useState(null);
    const [isAddressSelected, setIsAddressSelected] = useState(true);
    const [billingEditDialogOpen, setBillingEditDialogOpen] = useState(false);
    const [, forceUpdate] = useState(0);
    const selectedRowsRef = useRef({});
    selectedRowsRef.current = selectedRows;
    const orders = props.orders;
    const updateOrderFormState = props.updateOrderFormState;
    const orderChanged = props.orderChanged;
    const warehouses = props.warehouses;
    const [selectableWarehouses, setSelectableWarehouses] = useState([]);
    const [blockedDeliveryMethodIds, setBlockedDeliveryMethodIds] = useState([]);
    const { t } = useTranslation();

    const getInputErrors = useCallback(
        (values) => {
            const status = {
                toimitusaika:
                    values.deliveryTime == null ||
                    values.deliveryTime.length === 0 ||
                    !moment(values.deliveryTime).isValid() ||
                    moment(values.deliveryTime).isBefore(moment(props.earliestDeliveryDate), 'day'),
                aikainentoimitusaika:
                    values.orderType !== Order.OrderTypeUrgent() &&
                    (values.earlyDeliveryTime == null ||
                        values.earlyDeliveryTime.length === 0 ||
                        !moment(values.earlyDeliveryTime).isValid() ||
                        moment(values.earlyDeliveryTime).isAfter(moment(values.deliveryTime), 'day')),
                toimitusehdot: values.deliveryTerms == null || values.deliveryTerms.length === 0,
                'tilaaja-nimi': values.ordererName == null || values.ordererName.length === 0,
                'tilaaja-puh': values.ordererTel == null || values.ordererTel.length === 0,
                'order-reference': values.reference == null || values.reference.length === 0,
                'confirmation-email': values.confirmationEmail == null || values.confirmationEmail.length === 0,
                deliveryMethod: values.deliveryMethod == null || values.deliveryMethod.length === 0,
                deliveryAddress: !isAddressSelected,
                billingInformation:
                    (values.billingAddress == null || values.billingAddress.isValid() === false) &&
                    (values.electronicInvoiceAddress == null || values.electronicInvoiceAddress.isValid() === false),
                // Pallets are allocated manually. Orders may not be sent without an allocated pallet
                // group once the delivery times are set (not applicable when re-editing an already sent
                // order, since reallocation isn't possible for non-draft orders).
                palletInformation:
                    !props.editOrderedItem &&
                    some(
                        values.orderedFilters,
                        (of) => of.palletInformation == null || of.palletInformation.length === 0
                    ),
            };
            return status;
        },
        [props.earliestDeliveryDate, isAddressSelected, props.editOrderedItem]
    );

    const isFormOk = useCallback(() => {
        const status = getInputErrors(orders[0]);
        for (const k of Object.keys(status)) {
            if (status[k]) {
                return false;
            }
        }
        return true;
    }, [getInputErrors, orders]);

    useEffect(() => {
        setIsAddressSelected(!orders[0].deliveryAddressMustBeSelectedInCart);
    }, [orders]);

    const deliveryAddressZipCode = orders[0] && orders[0].deliveryAddress ? orders[0].deliveryAddress.zipCode : null;

    useEffect(() => {
        // When the parent (e.g. shoppingbasket.jsx, which already fetches this once per unique
        // zip code across the whole basket) supplies the blocked ids directly, use those instead
        // of each row independently re-fetching the same data - with many orders open at once
        // that previously meant one redundant backend call per row.
        if (props.blockedDeliveryMethodIds != null) {
            setBlockedDeliveryMethodIds(props.blockedDeliveryMethodIds);
            return;
        }
        let cancelled = false;
        const fetchBlockedDeliveryMethods = props.customerView
            ? getCustomerBlockedDeliveryMethods
            : getBlockedDeliveryMethods;
        fetchBlockedDeliveryMethods(deliveryAddressZipCode).then((ids) => {
            if (!cancelled) setBlockedDeliveryMethodIds(ids || []);
        });
        return () => {
            cancelled = true;
        };
    }, [deliveryAddressZipCode, props.customerView, props.blockedDeliveryMethodIds]);

    useEffect(() => {
        updateOrderFormState(isFormOk(), props.orders[0].id, getInputErrors(orders[0]));
    }, [props.orders, isFormOk, getInputErrors, orders, updateOrderFormState, props.editOrderedItem]);

    const saveNewAddress = useCallback(
        async (address) => {
            // only for delivery address for now.
            let newOrder = new Order(orders[0]);
            newOrder.deliveryAddress = address;
            newOrder.deliveryRecipient = !!address.name ? address.name : newOrder.deliveryRecipient;
            newOrder.deliveryAddressMustBeSelectedInCart = false;
            setAddressEditOpen(false);
            setCustomerSearchOpen(false);
            orderChanged(newOrder);
        },
        [orderChanged, orders]
    );

    useEffect(() => {
        if (selectedDeliveryCustomer !== null) {
            let customer = new Customer(props.customersById[selectedDeliveryCustomer]);
            let deliveryAddress = customer.deliveryAddress;
            if (!deliveryAddress.name) {
                deliveryAddress.name = customer.name;
            }
            saveNewAddress(deliveryAddress);
            setSelectedDeliveryCustomer(null);
        }
    }, [props.customersById, saveNewAddress, selectedDeliveryCustomer]);

    useEffect(() => {
        async function checkDeliveryDateValidity() {
            if (props.earliestDeliveryDate) {
                if (props.orders[0].deliveryTime && moment(props.orders[0].deliveryTime).isValid()) {
                    if (moment(props.orders[0].deliveryTime).isBefore(moment(props.earliestDeliveryDate), 'day')) {
                        setConfirmDeliveryTimeResetOpen(true);
                    }
                }
            } else if (props.orders[0].deliveryTime) {
                setConfirmDeliveryTimeResetOpen(true);
            }
        }
        if (props.earliestDeliveryDateAvailable) checkDeliveryDateValidity();
    }, [
        props.earliestDeliveryDate,
        props.earliestDeliveryDateDetails,
        props.earliestDeliveryDateAvailable,
        props.orders,
        props.editOrderedItem,
    ]);

    useEffect(() => {
        if (warehouses && props.orders[0].customer) {
            setSelectableWarehouses(
                warehouses.filter((w) => w.customer && w.customer.id === props.orders[0].customer.id)
            );
        }
    }, [warehouses, props.orders]);

    const removeMe = () => {
        setRemovingMe(true);
        props.removeOrder(props.orders[0].id);
    };

    const onOpenDeliveryDatePicker = () => {
        setIgnoreDateTimeChangeTimeoutHandle(
            setTimeout(() => {
                setIgnoreDateTimeChangeTimeoutHandle(null);
            }, 500)
        );
    };

    const onEarlyDeliveryDateChange = (date) => {
        const selectedDate = dayjs(date);
        let earlyDate = null;
        if (ignoreDateTimeChangeTimeoutHandle) {
            return;
        }

        if (selectedDate.isValid()) {
            earlyDate = selectedDate.hour(12).minute(0).second(0);
            if (dayjs(earlyDate).isAfter(dayjs(orders[0].deliveryTime))) {
                toast.error(t('order.earliestDeliveryDateCannotByLaterThanDeliveryDate'), { autoClose: 5000 });
                earlyDate = null;
            }
        }

        let earlyDeliveryTime = earlyDate ? earlyDate : null;
        if (earlyDeliveryTime && earlyDate instanceof dayjs) {
            earlyDeliveryTime = earlyDeliveryTime.utc().toISOString();
        }
        if (orders[0].earlyDeliveryTime !== earlyDeliveryTime) {
            const newOrder = new Order(orders[0]);
            newOrder.earlyDeliveryTime = earlyDeliveryTime;
            if (!props.editOrderedItem) {
                // Two orders with identical delivery info can have the same pallet allocation
                // but if the delivery times are changed afterwards, the pallet information
                // has to be allocated again. This does not apply once an order has already been
                // sent (editOrderedItem) - pallet grouping must not change after the fact.
                newOrder.orderedFilters.forEach((of) => (of.palletInformation = null));
            }
            saveChanges(newOrder);
        } else {
            forceUpdate((n) => n + 1);
        }
    };

    const onDateChange = async (date) => {
        let newDate = null;
        let earlyDate = orders[0].earlyDeliveryTime;
        const selectedDate = dayjs(date).hour(12).minute(0);
        if (ignoreDateTimeChangeTimeoutHandle) {
            return;
        }

        if (selectedDate.isValid()) {
            newDate = selectedDate;
            if (selectedDate.isBefore(dayjs())) {
                toast.error(t('order.msgCannotSetDeliveryDateIntoThePast'), { autoClose: 5000 });
                newDate = null;
            } else if (!props.earliestDeliveryDate) {
                toast.error(t('errors.capacityReservationFailsTooLongPeriod'), { autoClose: 5000 });
                newDate = null;
            } else if (props.earliestDeliveryDate && selectedDate.isBefore(dayjs(props.earliestDeliveryDate), 'day')) {
                // invalid date inputted into input box etc.
                toast.error(
                    t('order.earliestPossibleDeliveryDateIs', {
                        earliestDate: dayjs(props.earliestDeliveryDate).format('DD.MM.YYYY'),
                    }),
                    { autoClose: 5000 }
                );
                newDate = null;
            }
        } else {
            newDate = null;
        }

        if (props.customerView) {
            earlyDate = newDate;
        } else if (newDate === null) {
            earlyDate = null;
        } else if (dayjs(earlyDate).isSameOrAfter(dayjs(newDate), 'day')) {
            earlyDate = newDate;
        }

        let deliveryTime = newDate ? newDate : null;
        if (deliveryTime && deliveryTime instanceof dayjs) {
            deliveryTime = deliveryTime.utc().toISOString();
        }
        let earlyDeliveryTime = earlyDate ? earlyDate : null;
        if (earlyDeliveryTime && earlyDate instanceof dayjs) {
            earlyDeliveryTime = earlyDeliveryTime.utc().toISOString();
        }
        if (orders[0].deliveryTime !== deliveryTime || orders[0].earlyDeliveryTime !== earlyDeliveryTime) {
            const newOrder = new Order(orders[0]);
            newOrder.deliveryTime = deliveryTime;
            newOrder.earlyDeliveryTime = earlyDeliveryTime;
            if (!props.editOrderedItem) {
                // Two orders with identical delivery info can have the same pallet allocation
                // but if the delivery times are changed afterwards, the pallet information
                // has to be allocated again. This does not apply once an order has already been
                // sent (editOrderedItem) - pallet grouping must not change after the fact.
                newOrder.orderedFilters.forEach((of) => (of.palletInformation = null));
            }
            saveChanges(newOrder);
        } else {
            forceUpdate((n) => n + 1);
        }
    };

    const onChange = (event) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;
        const newOrder = new Order(orders[0]);
        switch (name) {
            case 'toimitusehdot':
                newOrder.deliveryTerms = value;
                break;
            case 'tilaaja-nimi':
                newOrder.ordererName = value;
                break;
            case 'tilaaja-puh':
                newOrder.ordererTel = value;
                break;
            case 'order-reference':
                newOrder.reference = value;
                break;
            case 'order-packinginstructions':
                newOrder.packingInstructions = value;
                break;
            case 'OrderType':
                newOrder.orderType = value;
                newOrder.deliveryTime = null;
                newOrder.earlyDeliveryTime = null;
                if (!props.editOrderedItem) {
                    newOrder.orderedFilters.forEach((of) => (of.palletInformation = null));
                }
                break;
            case 'order-generalinfo':
                newOrder.generalInfo = value;
                break;
            case 'confirmation-email':
                newOrder.confirmationEmail = value;
                break;
            case 'DeliveryMethod':
                const param = props.parameters['DeliveryMethod'].find((p) => p.id === value);
                newOrder.deliveryMethod = param ? param.value : null;
                newOrder.deliveryTime = null;
                newOrder.earlyDeliveryTime = null;
                if (!props.editOrderedItem) {
                    newOrder.orderedFilters.forEach((of) => (of.palletInformation = null));
                }
                break;
            case 'delivery-confirmation-email':
                newOrder.deliveryConfirmationEmail = value;
                break;
            case 'Warehouse':
                newOrder.orderToWarehouseId = value;
                break;
            default:
                break;
        }
        saveChanges(newOrder);
    };

    const onPhonenumberBlur = (event) => {
        const target = event.target;
        const value = target.value;
        const newOrder = new Order(orders[0]);
        newOrder.ordererTel = normalizePhonenumber(value);
        saveChanges(newOrder);
    };

    const saveChanges = (newOrder) => {
        props.orderChanged(newOrder);
    };

    const cancelDelete = () => {
        setConfirmDeleteRowsOpen(false);
    };

    const confirmDelete = () => {
        const removeOrders = [];
        for (let ord of props.orders) {
            let newOrder = new Order(ord);
            let filtOrders = [];
            let doSave = false;
            let removeOrder = true;
            for (let o of newOrder.orderedFilters) {
                if (!rowIsSelected(o.id)) {
                    filtOrders.push(o);
                    removeOrder = false;
                } else {
                    doSave = true;
                }
            }
            if (removeOrder) removeOrders.push(ord.id);
            if (doSave) {
                newOrder.orderedFilters = filtOrders;
                props.orderChanged(newOrder);
            }
        }
        if (removeOrders.length) {
            removeOrders.forEach((ordId) => props.removeOrder(ordId));
        }
        setConfirmDeleteRowsOpen(false);
        setSelectedRows({});
    };

    const onClickSelectAllRows = (e) => {
        let target = e.target;
        let newSelectedRows = clone(selectedRows);
        const orderedFiltersIds = flatten(props.orders.map((o) => o.orderedFilters)).map((o) => o.id);
        for (let fId of orderedFiltersIds) {
            newSelectedRows[fId] = target.checked;
        }
        let select = {};
        //calculate from all orders
        for (let o of props.orders) {
            select[o.id] = target.checked;
        }
        setOrdersSelected(select);
        setSelectedRows(newSelectedRows);
        setAllSelected(!allSelected);
        setRowInEditId(null);
    };

    const onClickSelectOrderAllRows = (e, orderId) => {
        let target = e.target;
        let newSelectedRows = clone(selectedRows);
        const targetOrder = find(props.orders, { id: orderId });
        const orderedFiltersIds = targetOrder.orderedFilters.map((o) => o.id);
        for (let fId of orderedFiltersIds) {
            newSelectedRows[fId] = target.checked;
        }
        setSelectedRows(newSelectedRows);
        setRowInEditId(null);
        let newOrderRowsSelected = clone(ordersSelected);
        newOrderRowsSelected[orderId] = target.checked;
        setOrdersSelected(newOrderRowsSelected);
    };

    const rowIsSelected = (id) => {
        return selectedRows[id] != null && selectedRows[id] === true;
    };

    const allOrdersIsSelected = () => {
        for (let o of props.orders) {
            if (ordersSelected[o.id] === undefined || ordersSelected[o.id] !== true) return false;
        }
        return true;
    };

    const onClickRow = (id, editAllowed) => {
        setRowInEditId(id);
    };

    const filterOrderChanged = (newFo) => {
        let orderToEdit = null;
        for (let o of orders) {
            const row = o.orderedFilters.find((r) => r.id === newFo.id);
            if (row) {
                orderToEdit = o;
                break;
            }
        }
        if (!orderToEdit) throw new Error('fail');

        const newOrder = new Order(orderToEdit);
        newOrder.orderedFilters = newOrder.orderedFilters.map((row) => {
            if (row.id === newFo.id) {
                return newFo;
            }
            return row;
        });
        newOrder.updateTotalPrice();
        props.orderChanged(newOrder);
    };

    const onClickSelect = (id) => {
        const newSelectedRows = Object.assign({}, selectedRowsRef.current);

        newSelectedRows[id] = !newSelectedRows[id];
        setSelectedRows(newSelectedRows);
        let newOrderRowsSelected = clone(ordersSelected);
        for (let ord of props.orders) {
            if (some(ord.orderedFilters, { id: id })) {
                if (ord.orderedFilters.every((filtOrd) => newSelectedRows[filtOrd.id] === newSelectedRows[id])) {
                    newOrderRowsSelected[ord.id] = newSelectedRows[id];
                } else {
                    newOrderRowsSelected[ord.id] = false;
                }
                setOrdersSelected(newOrderRowsSelected);
            }
        }
    };

    const closeRowEdit = () => {
        setRowInEditId(null);
    };

    const removeSelectedRows = () => {
        let notAllowed = true;

        for (let ord of props.orders) {
            for (let o of ord.orderedFilters) {
                if (!rowIsSelected(o.id)) {
                    notAllowed = false;
                }
            }
            if (!props.editOrderedItem && notAllowed) break;
        }

        if (notAllowed) {
            setAlertDialogOpen(true);
        } else {
            setConfirmDeleteRowsOpen(true);
        }
    };

    const anyRowFromOrderSelected = (skipDeliveryFeeRows = false) => {
        let orderedFiltersIds = [];
        for (let ord of props.orders) {
            if (skipDeliveryFeeRows)
                orderedFiltersIds.push(
                    ord.orderedFilters
                        .filter(
                            (o) => o.productBundle || (o.product.productCode !== 'R' && o.product.productCode !== 'RP')
                        )
                        .map((o) => o.id)
                );
            else orderedFiltersIds.push(ord.orderedFilters.map((o) => o.id));
        }
        orderedFiltersIds = flatten(orderedFiltersIds);
        for (let fId of orderedFiltersIds) {
            if (selectedRows[fId] === true) return true;
        }
        return false;
    };

    const editDeliveryAddress = () => {
        if (!orders[0].deliveryAddress.name) {
            orders[0].deliveryAddress.name = orders[0].deliveryRecipient;
        }
        setAddressEditOpen(true);
        setCustomerSearchOpen(false);
    };

    const editBillingInformation = () => {
        setBillingEditDialogOpen(true);
    };

    const onCancelBillingInformationEdit = () => {
        setBillingEditDialogOpen(false);
    };

    const onSaveBillingInformationEdit = (billingAddress, einvoiceAddress) => {
        let newOrder = new Order(orders[0]);
        // if the billing address is null or valid, save it
        if (!billingAddress || billingAddress.isValid() === true) {
            newOrder.billingAddress = billingAddress;
        } else {
            // with invalid address show toast error
            toast.error(t('order.billingAddressInvalid'), { autoClose: 5000 });
        }
        // if the e-invoice address is null or valid, save it
        if (!einvoiceAddress || einvoiceAddress.isValid() === true) {
            newOrder.electronicInvoiceAddress = einvoiceAddress;
        } else {
            // with invalid e-invoice address show toast error
            toast.error(t('order.eInvoiceAddressInvalid'), { autoClose: 5000 });
        }

        orderChanged(newOrder);
        setBillingEditDialogOpen(false);
    };

    const searchCustomer = () => {
        setCustomerSearchOpen(!customerSearchOpen);
    };

    const getCustomerFullData = async (customerId) => {
        if (!props.customersById[customerId]) await dispatch(getCustomerFull(customerId));
    };

    const customerSelectedInternal = async (customerId) => {
        if (!props.customersById[customerId]) {
            await getCustomerFullData(customerId);
            setSelectedDeliveryCustomer(customerId);
        } else {
            let customer = new Customer(props.customersById[customerId]);
            let deliveryAddress = customer.deliveryAddress;
            if (!deliveryAddress.name) {
                deliveryAddress.name = customer.name;
            }
            saveNewAddress(deliveryAddress);
        }
    };

    const customerSelected = (customerId) => {
        customerSelectedInternal(customerId);
    };

    const getParametersObject = (type, text) => {
        return props.parameters[type].find((p) => p.value === text);
    };

    const onEditPalletInfo = () => {
        props.editFoPalletInfo(orders[0].orderedFilters.filter((fo) => rowIsSelected(fo.id)).map((fo) => fo.id));
    };

    const customersWithDeliveryInfo =
        props.customers !== null && props.customers.length > 0
            ? filter(props.customers, (c) => c != null && c.deliveryAddressId != null)
            : [];

    const addDelivery = () => {
        props.addProductWithCode(orders[0].id, 'R');
    };

    const addDeliveryAndPackagingContractPrice = () => {
        props.addProductWithCode(orders[0].id, 'RP');
    };
    const productSelected = (prodId) => {
        props.addProductWithId(addProdToOrderId, prodId);
        setAddNewProdDialogOpen(false);
        setAddProdToOrderId(null);
    };
    const addProduct = (orderId) => {
        setAddProdToOrderId(orderId);
        setAddNewProdDialogOpen(true);
    };

    const ordersTotalPrice = () => {
        return sumBy(props.orders, function (o) {
            return o.orderTotalPrice;
        });
    };

    const ordersVatAmount = () => {
        return sumBy(props.orders, function (o) {
            return o.vatAmount;
        });
    };

    const ordersTotalPriceWithoutVat = () => {
        return sumBy(props.orders, function (o) {
            return o.orderTotalPriceWithoutVat;
        });
    };

    const isChecked = (orderId) => {
        if (ordersSelected[orderId]) return ordersSelected[orderId];
        return false;
    };

    const confirmResetDeliveryTime = () => {
        const newOrder = new Order(orders[0]);
        newOrder.deliveryTime = null;
        if (!props.editOrderedItem) {
            newOrder.orderedFilters.forEach((of) => (of.palletInformation = null));
        }
        saveChanges(newOrder);

        setTimeout(() => {
            setConfirmDeliveryTimeResetOpen(false);
        }, 200);
    };

    const alertDialogConfirm = () => {
        setAlertDialogOpen(false);
    };

    const deliveryTimeMissing = () => {
        if (orders[0].orderType === Order.OrderTypeUrgent()) {
            return !orders[0].deliveryTime;
        }
        return !orders[0].deliveryTime || !orders[0].earlyDeliveryTime;
    };

    return (
        <Grid container sx={{ width: '100%', marginBottom: '0.5em' }}>
            <Grid item xs={12}>
                <Grid container>
                    {!props.editOrderedItem && (
                        <Grid item xs={12}>
                            <DeleteButton
                                onSubmit={removeMe}
                                name="remove-order"
                                deleting={removingMe}
                                disabled={removingMe}
                            />
                        </Grid>
                    )}
                    {!props.customerView && (
                        <Grid item container>
                            <Grid item xs={12} md={4}>
                                <strong>{t('general.portfolio')}</strong>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                {props.portfolio.name}
                            </Grid>
                        </Grid>
                    )}

                    {props.editOrderedItem && (
                        <Grid item container>
                            <Grid item xs={12} md={4}>
                                <strong>{t('general.orderNumber')}</strong>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                {getOrderDisplayNumber(orders[0])}
                            </Grid>
                        </Grid>
                    )}

                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('general.businessId')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {orders[0].billingResponsibleBusinessId}
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('general.vatId')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {orders[0].billingResponsibleVatId}
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('general.customerNumber')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {orders[0].billingResponsibleCustomerNumber}
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('parameterCategories.ContractNumber')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {orders[0].contractNumber}
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('parameterCategories.OurReference')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {orders[0].ourReference}
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('parameterCategories.InvoicingTerms')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {orders[0].billingTerms}
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('general.invoicingAddress')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {orders[0].billingResponsible}
                            {orders[0].billingAddress && (
                                <div>
                                    {orders[0].billingAddress.streetAddress},&nbsp;
                                    {orders[0].billingAddress.zipCode}&nbsp;
                                    {orders[0].billingAddress.city}
                                </div>
                            )}
                        </Grid>
                    </Grid>
                    {orders[0].electronicInvoiceAddress && (
                        <Grid item container>
                            <Grid item xs={12} md={4}>
                                <strong>{t('general.eInvoiceAddress')}</strong>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                {orders[0].electronicInvoiceAddress && (
                                    <div>
                                        {orders[0].electronicInvoiceAddress.ovt},&nbsp;
                                        {orders[0].electronicInvoiceAddress.operator},&nbsp;
                                        {orders[0].electronicInvoiceAddress.operatorBrokerId}
                                    </div>
                                )}
                                {!orders[0].electronicInvoiceAddress && <div>-</div>}
                            </Grid>
                        </Grid>
                    )}
                    <Grid item container>
                        <Grid item xs={12} md={4}></Grid>
                        <Grid item xs={12} md={8}>
                            <Alert severity="info">{t('order.modifyBillingInformation')}</Alert>
                            <Tooltip title={t('order.editBillingAddress')}>
                                <Button onClick={editBillingInformation}>
                                    <EditIcon />
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    {orders[0].emailInvoiceAddress != null && orders[0].emailInvoiceAddress.length > 0 && (
                        <Grid item container>
                            <Grid item xs={12} md={4}>
                                <strong>{t('invoice.emailInvoiceAddress')}</strong>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <div>{orders[0].emailInvoiceAddress}</div>
                            </Grid>
                        </Grid>
                    )}
                    <Grid item container>
                        <Grid item xs={12} md={4}>
                            <strong>{t('general.deliveryCustomerNumber')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <div>{orders[0].deliveryCustomerNumber}</div>
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Grid item xs={12} md={4} sx={isAddressSelected ? {} : { color: 'red' }}>
                            <strong>{t('general.deliveryAddress')}</strong>
                        </Grid>
                        <Grid item xs={12} md={8} sx={isAddressSelected ? {} : { color: 'red' }}>
                            {props.orders && props.orders.length > 1 && props.orders[0].orderToPrivateCustomer ? (
                                <span>...</span>
                            ) : (
                                <React.Fragment>
                                    <div>
                                        {orders[0].deliveryRecipient}
                                        <br />
                                        {orders[0].deliveryAddress.streetAddress},&nbsp;
                                        {orders[0].deliveryAddress.zipCode}&nbsp;
                                        {orders[0].deliveryAddress.city}
                                    </div>
                                    <span>
                                        <Tooltip title={t('order.editDeliveryAddress')}>
                                            <Button onClick={editDeliveryAddress}>
                                                <EditIcon />
                                            </Button>
                                        </Tooltip>
                                        {!props.customerView && (
                                            <Tooltip title={t('order.copyDeliveryAddressFromCustomer')}>
                                                <Button onClick={searchCustomer} name="btn-search-customer">
                                                    <SearchIcon />
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {customerSearchOpen && (
                                            <CustomerAutoComplete
                                                customers={customersWithDeliveryInfo}
                                                selected={customerSelected}
                                            />
                                        )}
                                    </span>
                                </React.Fragment>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DeliveryDateDetails deliveryDateDetails={props.earliestDeliveryDateDetails} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DeliveryDatePicker
                            label={t('general.deliveryDate') + ' *'}
                            value={orders[0].deliveryTime}
                            onChange={onDateChange}
                            id={'toimitusaika-' + orders[0].id}
                            earliestDeliveryDate={props.earliestDeliveryDate}
                            onOpen={onOpenDeliveryDatePicker}
                            name="deliveryTime"
                            deliveryMethod={orders[0].deliveryMethod}
                        />
                    </Grid>

                    {!props.customerView && (
                        <Grid item xs={12} md={6}>
                            <OrderTypeSelector
                                handleChange={(value) => onChange({ target: { value, name: 'OrderType' } })}
                                selected={orders[0].orderType}
                            />
                        </Grid>
                    )}
                    {!props.customerView && orders[0].orderType !== Order.OrderTypeUrgent() && (
                        <Grid item xs={12} md={6}>
                            <DeliveryDatePicker
                                label={t('order.earliestDeliveryDate') + ' *'}
                                value={orders[0].earlyDeliveryTime}
                                onChange={onEarlyDeliveryDateChange}
                                id={'aikaisintoimitusaika-' + orders[0].id}
                                earliestDeliveryDate={props.earliestDeliveryDate}
                                latestDeliveryDate={orders[0].deliveryTime}
                                onOpen={onOpenDeliveryDatePicker}
                                disabled={!moment(orders[0].deliveryTime).isValid()}
                                name="earlyDeliveryTime"
                                enableAllWeekDays={true}
                            />
                        </Grid>
                    )}
                    {selectableWarehouses.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <WarehouseSelector
                                selectedWarehouseId={orders[0].orderToWarehouseId}
                                warehouses={selectableWarehouses}
                                handleChange={(value) => onChange({ target: { value, name: 'Warehouse' } })}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <ParameterSelector
                            values={props.parameters}
                            category={'DeliveryMethod'}
                            selected={getParametersObject('DeliveryMethod', orders[0].deliveryMethod)}
                            handleChange={(type, value) => onChange({ target: { value, name: 'DeliveryMethod' } })}
                            required={true}
                            blockedIds={blockedDeliveryMethodIds}
                            blockedMessage={t('cargo.deliveryMethodBlockedReason')}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            disabled={orders[0].deliveryTermsImmutable === true}
                            required
                            error={getInputErrors(orders[0])['toimitusehdot']}
                            id={'toimitusehdot-' + orders[0].id}
                            name="toimitusehdot"
                            label={t('parameterCategories.DeliveryTerms')}
                            sx={styles.textField}
                            margin="normal"
                            value={orders[0].deliveryTerms}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            error={getInputErrors(orders[0])['tilaaja-nimi']}
                            id={'tilaaja-nimi-' + orders[0].id}
                            name="tilaaja-nimi"
                            label={t('order.ordererName')}
                            value={orders[0].ordererName}
                            sx={styles.textField}
                            margin="normal"
                            onChange={onChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            error={getInputErrors(orders[0])['tilaaja-puh']}
                            id={'tilaaja-puh-' + orders[0].id}
                            name="tilaaja-puh"
                            label={t('order.deliveryRecipientTel')}
                            value={orders[0].ordererTel}
                            sx={styles.textField}
                            margin="normal"
                            onChange={onChange}
                            onBlur={onPhonenumberBlur}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <EmailInput
                            required={true}
                            label={t('order.confirmationEmail')}
                            error={getInputErrors(orders[0])['confirmation-email']}
                            name="confirmation-email"
                            domElemName="confirmation-email"
                            onChange={(value) => {
                                onChange({
                                    target: {
                                        value: value,
                                        name: 'confirmation-email',
                                    },
                                });
                            }}
                            emails={orders[0].confirmationEmail}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <EmailInput
                            required={false}
                            label={t('order.deliveryConfirmationMail')}
                            error={false}
                            name="delivery-confirmation-email"
                            domElemName="delivery-confirmation-email"
                            onChange={(value) => {
                                onChange({
                                    target: {
                                        value: value,
                                        name: 'delivery-confirmation-email',
                                    },
                                });
                            }}
                            emails={orders[0].deliveryConfirmationEmail}
                            hint={t('order.deliveryConfirmationHint')}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            error={getInputErrors(orders[0])['order-reference']}
                            id={'order-reference-' + orders[0].id}
                            name="order-reference"
                            label={t('general.yourReference')}
                            value={orders[0].reference}
                            sx={styles.textField}
                            margin="normal"
                            onChange={onChange}
                        />
                    </Grid>

                    {!props.customerView && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                multiline={true}
                                id={'order-packinginstructions-' + orders[0].id}
                                name="order-packinginstructions"
                                label={t('general.packingInstructions')}
                                value={!!orders[0].packingInstructions ? orders[0].packingInstructions : ''}
                                sx={styles.textField}
                                margin="normal"
                                onChange={onChange}
                            />
                        </Grid>
                    )}
                    {!props.customerView && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                multiline={true}
                                id={'order-generalinfo-' + orders[0].id}
                                name="order-generalinfo"
                                label={t('order.generalInfoToProduction')}
                                value={!!orders[0].generalInfo ? orders[0].generalInfo : ''}
                                sx={styles.textField}
                                margin="normal"
                                onChange={onChange}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{ pt: '2em', pb: '2em', width: '100%' }}>
                        <table className="table table-sm">
                            <thead>
                                {!props.customerView && (
                                    <Box component="tr" sx={{ display: { xs: 'none', md: 'table-row' } }}>
                                        <th>
                                            <Checkbox
                                                sx={{ p: '0px' }}
                                                checked={allSelected}
                                                indeterminate={allSelected === !allOrdersIsSelected()}
                                                onChange={(e) => onClickSelectAllRows(e)}
                                                name="check-all"
                                            />
                                        </th>
                                        <th>{t('location.machine')}</th>
                                        <th>{t('order.filter')}</th>
                                        <th>{t('pcs')}</th>
                                        <th>{t('order.pricePerUnitWithDiscountVat0')}</th>
                                        <th>{t('invoice.totalPriceVatZero')}</th>
                                    </Box>
                                )}
                                <Box component="tr" sx={{ display: { xs: 'table-row', md: 'none' } }}>
                                    <th>
                                        <Checkbox
                                            sx={{ p: '0px' }}
                                            checked={allSelected}
                                            indeterminate={allSelected === !allOrdersIsSelected()}
                                            onChange={(e) => onClickSelectAllRows(e)}
                                            name="check-all"
                                        />
                                    </th>
                                    <th>{t('order.filter')}</th>
                                </Box>
                                {props.customerView && (
                                    <Box component="tr" sx={{ display: { xs: 'none', md: 'table-row' } }}>
                                        <th>
                                            <Checkbox
                                                sx={{ p: '0px' }}
                                                checked={allSelected}
                                                indeterminate={allSelected === !allOrdersIsSelected()}
                                                onChange={(e) => onClickSelectAllRows(e)}
                                                name="check-all"
                                            />
                                        </th>
                                        <th>{t('location.machine')}</th>
                                        <th>{t('order.filter')}</th>
                                        <th>{t('pcs')}</th>
                                        {props.allowPricing && (
                                            <React.Fragment>
                                                <th>{t('order.pricePerUnitVat0')}</th>
                                                <th>{t('invoice.totalPriceVatZero')}</th>
                                            </React.Fragment>
                                        )}
                                    </Box>
                                )}
                            </thead>
                            <tbody>
                                {props.orders.map((orderitem) => (
                                    <React.Fragment key={orderitem.id}>
                                        {props.orders.length > 1 && (
                                            <tr>
                                                <td style={{ background: 'lightgray' }}>
                                                    <Checkbox
                                                        sx={{ p: '0px 0px 0px 2px' }}
                                                        checked={isChecked(orderitem.id)}
                                                        onChange={(e) => onClickSelectOrderAllRows(e, orderitem.id)}
                                                        name="edit-check-all"
                                                    />
                                                </td>
                                                <td colSpan={5} style={{ background: 'lightgray' }}>
                                                    <strong>
                                                        {orderitem.location
                                                            ? orderitem.location.name
                                                            : orderitem.getDeliveryInfo()}
                                                    </strong>
                                                </td>
                                            </tr>
                                        )}
                                        {orderitem.orderedFilters
                                            .filter(function (f) {
                                                return f.product &&
                                                    (f.product.productCode === 'R' || f.product.productCode === 'RP')
                                                    ? false
                                                    : true;
                                            })
                                            .map((f, ind) => (
                                                <FilterOrder
                                                    index={ind}
                                                    key={f.id}
                                                    customerView={props.customerView}
                                                    filterOrder={f}
                                                    selected={selectedRows[f.id]}
                                                    clickRow={(id) => onClickRow(id, props.editOrderedItem)}
                                                    clickSelect={onClickSelect}
                                                    allowPricing={props.allowPricing}
                                                    editFoPalletInfo={props.editFoPalletInfo}
                                                    editOrderedItem={props.editOrderedItem}
                                                    editRow={rowInEditId === f.id}
                                                    fadeRow={!!rowInEditId}
                                                    closeRowEdit={closeRowEdit}
                                                    filterOrderChanged={filterOrderChanged}
                                                    priceOnly={false}
                                                    currency={orderitem.currency}
                                                />
                                            ))}
                                        <tr>
                                            <td>
                                                {!props.customerView && (
                                                    <Button
                                                        sx={{ mt: 1, mb: 1 }}
                                                        variant="contained"
                                                        color="secondary"
                                                        name="addproduct"
                                                        onClick={() => addProduct(orderitem.id)}
                                                        startIcon={<AddBoxIcon />}>
                                                        {t('general.product')}
                                                    </Button>
                                                )}
                                            </td>
                                            <td>
                                                {!props.editOrderedItem && (
                                                    <Tooltip
                                                        title={
                                                            props.autosaving
                                                                ? t('order.autosavingInProgress')
                                                                : orderitem.deliveryTime === null ||
                                                                    (orderitem.orderType !== Order.OrderTypeUrgent() &&
                                                                        orderitem.earlyDeliveryTime === null)
                                                                  ? t('order.deliveryTimesRequiredForPalletAllocation')
                                                                  : t('order.allocatePalletsButtonDescription')
                                                        }>
                                                        <span>
                                                            <Button
                                                                sx={{ mt: 1, mb: 1, whiteSpace: 'nowrap' }}
                                                                variant="contained"
                                                                color="secondary"
                                                                name="allocatePallets"
                                                                disabled={
                                                                    orderitem.deliveryTime === null ||
                                                                    (orderitem.orderType !== Order.OrderTypeUrgent() &&
                                                                        orderitem.earlyDeliveryTime === null) ||
                                                                    props.autosaving
                                                                }
                                                                onClick={() =>
                                                                    dispatch(
                                                                        props.customerView
                                                                            ? allocatePalletsForCustomerOrders([
                                                                                  orderitem.id,
                                                                              ])
                                                                            : allocatePalletsForOrders([orderitem.id])
                                                                    )
                                                                }
                                                                startIcon={<Inventory2Icon />}>
                                                                {t('buttons.allocatePallets')}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                                {props.orders.map((orderitem) => (
                                    <React.Fragment key={orderitem.id}>
                                        {orderitem.orderedFilters
                                            .filter(function (f) {
                                                return !f.productBundle &&
                                                    (f.product.productCode === 'R' || f.product.productCode === 'RP')
                                                    ? true
                                                    : false;
                                            })
                                            .map((f) => (
                                                <FilterOrder
                                                    key={f.id}
                                                    customerView={props.customerView}
                                                    filterOrder={f}
                                                    selected={selectedRows[f.id]}
                                                    clickRow={(id) => onClickRow(id, true)}
                                                    clickSelect={onClickSelect}
                                                    allowPricing={props.allowPricing}
                                                    editRow={rowInEditId === f.id}
                                                    fadeRow={!!rowInEditId}
                                                    closeRowEdit={closeRowEdit}
                                                    filterOrderChanged={filterOrderChanged}
                                                    priceOnly={true}
                                                    currency={orderitem.currency}
                                                />
                                            ))}
                                    </React.Fragment>
                                ))}
                                <tr></tr>
                                {props.allowPricing && (
                                    <React.Fragment>
                                        <Box
                                            component="tr"
                                            sx={{ display: { xs: 'none', md: 'table-row' }, fontWeight: 'bold' }}>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td style={{ fontWeight: 'bold' }}>{t('invoice.totalPriceVatZero')}</td>
                                            <td style={{ fontWeight: 'bold' }}>
                                                {formatCurrency(ordersTotalPriceWithoutVat(), 2, orders[0].currency)}
                                            </td>
                                        </Box>
                                        <Box
                                            component="tr"
                                            sx={{ display: { xs: 'none', md: 'table-row' }, fontWeight: 'bold' }}>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td style={{ fontWeight: 'bold' }}>
                                                {t('general.vatShort')} {orders[0].vatPercent}%
                                            </td>
                                            <td style={{ fontWeight: 'bold' }}>
                                                {formatCurrency(ordersVatAmount(), 2, orders[0].currency)}
                                            </td>
                                        </Box>
                                        <Box
                                            component="tr"
                                            sx={{ display: { xs: 'none', md: 'table-row' }, fontWeight: 'bold' }}>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td style={{ fontWeight: 'bold' }}>{t('invoice.totalPriceVatIncl')}</td>
                                            <td style={{ fontWeight: 'bold' }}>
                                                {formatCurrency(ordersTotalPrice(), 2, orders[0].currency)}
                                            </td>
                                        </Box>
                                        <Box component="tr" sx={{ display: { xs: 'table-row', md: 'none' } }}>
                                            <td></td>
                                            <td></td>
                                        </Box>
                                        <Box
                                            component="tr"
                                            sx={{ display: { xs: 'table-row', md: 'none' }, fontWeight: 'bold' }}>
                                            <td></td>
                                            <td>
                                                {t('invoice.totalPriceVatZero')}:{' '}
                                                <strong>
                                                    {formatCurrency(ordersTotalPriceWithoutVat(), 2, orders[0].currency)}
                                                </strong>
                                            </td>
                                        </Box>
                                        <Box
                                            component="tr"
                                            sx={{ display: { xs: 'table-row', md: 'none' }, fontWeight: 'bold' }}>
                                            <td></td>
                                            <td>
                                                {t('general.vatShort')} {orders[0].vatPercent}%:{' '}
                                                <strong>{formatCurrency(orders[0].vatAmount, 2, orders[0].currency)}</strong>
                                            </td>
                                        </Box>
                                        <Box
                                            component="tr"
                                            sx={{ display: { xs: 'table-row', md: 'none' }, fontWeight: 'bold' }}>
                                            <td></td>
                                            <td>
                                                {t('invoice.totalPriceVatIncl')}:{' '}
                                                <strong>{formatCurrency(ordersTotalPrice(), 2, orders[0].currency)}</strong>
                                            </td>
                                        </Box>
                                        {props.customerView &&
                                            orders[0].customer &&
                                            orders[0].customer.id === 184612 && (
                                                <tr>
                                                    <td colSpan={2}>
                                                        <em>&#42;) Models so marked are not Eurovent certified </em>
                                                    </td>
                                                </tr>
                                            )}
                                    </React.Fragment>
                                )}
                                <tr>
                                    <td colSpan="7">
                                        {anyRowFromOrderSelected() && (
                                            <Grid item container spacing={1}>
                                                <Grid item>
                                                    <DeleteButton
                                                        onSubmit={removeSelectedRows}
                                                        name={'btn-remove-selected'}
                                                    />
                                                </Grid>
                                                {!props.editOrderedItem &&
                                                    anyRowFromOrderSelected(true) &&
                                                    !deliveryTimeMissing() && (
                                                        <Grid item>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                startIcon={<EditIcon />}
                                                                onClick={onEditPalletInfo}>
                                                                {t('order.palletGroup')}
                                                            </Button>
                                                        </Grid>
                                                    )}
                                            </Grid>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Grid>

                    {!props.customerView && (
                        <Grid item xs={12} md={6}>
                            <Tooltip title={t('order.addFreight')}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    name="adddeliverycharge"
                                    onClick={addDelivery}
                                    startIcon={<AddBoxIcon />}>
                                    R
                                </Button>
                            </Tooltip>
                            &nbsp;
                            <Tooltip title={t('order.addFreightAndPackagingContractPrice')}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    name="adddeliverychargescontractprice"
                                    onClick={addDeliveryAndPackagingContractPrice}
                                    startIcon={<AddBoxIcon />}>
                                    RP
                                </Button>
                            </Tooltip>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            <DeleteConfirmDialog
                open={confirmDeleteRowsOpen}
                itemToDelete={t('order.selectedRowsFromOrder')}
                confirmDelete={confirmDelete}
                cancel={cancelDelete}
            />
            <AddressEditDialog
                address={orders[0].deliveryAddress}
                open={addressEditOpen}
                cancel={() => setAddressEditOpen(false)}
                save={saveNewAddress}
            />
            <BillingEditDialog
                dialogOpen={billingEditDialogOpen}
                cancel={onCancelBillingInformationEdit}
                save={onSaveBillingInformationEdit}
                billingAddress={orders[0].billingAddress}
                einvoiceAddress={orders[0].electronicInvoiceAddress}
            />
            <ConfirmationDialog
                open={confirmDeliveryTimeResetOpen}
                confirm={confirmResetDeliveryTime}
                cancel={confirmResetDeliveryTime}
                confirmText={t('buttons.close')}
                hideCancel={true}>
                {t('order.notEnoughCapacityDueToChangeInFilters')}
            </ConfirmationDialog>
            <ConfirmationDialog
                open={alertDialogOpen}
                confirm={alertDialogConfirm}
                cancel={alertDialogConfirm}
                confirmText={t('buttons.close')}
                hideCancel={true}>
                {t('order.cannotRemoveAllRowsFromOrder')}
            </ConfirmationDialog>
            <FilterProductSelectDialog showBundles={true} show={addNewProdDialogOpen} selected={productSelected} />
        </Grid>
    );
}
